// Supply targets vs received mass for this UTC week and month
// (GET /organization/:org/recycler-appetite-fulfillment). Mirrors the backend's
// get_recycler_appetite_fulfillment_: received = completed, not-rejected pickups
// delivered to the recycler in the window (plus the other suppliers' deliveries,
// see deliveries.ts); targets are converted between weekly and monthly by days.

import type {
  RecyclerAppetiteFulfillmentLine,
  RecyclerAppetiteFulfillmentPeriod,
  RecyclerAppetiteFulfillmentResponse,
  RecyclerAppetiteStored,
} from '../../types/recycler-appetite.type';
import { DAY_MS, categoryByCode, materialById, mondayOf, monthStart, naiveIso } from '../core';
import { supplierReceivedKg } from './deliveries';
import { getAppetite } from './organizations';
import { allPickups, countsAsReceived, pickupFulfilledKg } from './pickups';
import { toKg } from './preOrders';

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

/** target_kg_for_appetite_line */
export function targetKgForLine(
  quantity: number,
  unit: string,
  repeat: string,
  period: 'week' | 'month',
  daysInMonth: number,
): number {
  const base = toKg(quantity, unit);
  if (repeat === 'monthly') return period === 'month' ? base : base * (7 / daysInMonth);
  return period === 'week' ? base : base * (daysInMonth / 7);
}

/** Received kg per material id for the recycler in [start, end). */
export function receivedKgByMaterial(recyclerOrgId: string, startMs: number, endMs: number, now = Date.now()): Record<string, number> {
  const totals = supplierReceivedKg(startMs, endMs, now);
  for (const p of allPickups()) {
    if (p.recycler_org_id !== recyclerOrgId || !countsAsReceived(p) || !p.completed_at) continue;
    const at = Date.parse(p.completed_at);
    if (at < startMs || at >= endMs || at > now) continue;
    totals[p.material_id] = (totals[p.material_id] ?? 0) + pickupFulfilledKg(p);
  }
  return totals;
}

function meta(materialId: string) {
  const material = materialById(materialId);
  return {
    name: material?.name_en ?? null,
    catalog_material_id: material?.id ?? null,
    category_code: material ? categoryByCode(material.category_code)?.code ?? material.category_code : null,
    parent_category: material?.category_code ?? 'other',
  };
}

function buildPeriod(
  period: 'week' | 'month',
  startMs: number,
  endMs: number,
  daysInMonth: number,
  config: RecyclerAppetiteStored,
  received: Record<string, number>,
): RecyclerAppetiteFulfillmentPeriod {
  const accepts = config.accepts_additional_quantity !== false;
  const repeat = config.repeat ?? 'weekly';
  const lines: RecyclerAppetiteFulfillmentLine[] = [];
  const targeted = new Set<string>();

  for (const line of config.lines ?? []) {
    targeted.add(line.material_id);
    const targetKg = targetKgForLine(line.quantity, line.unit, repeat, period, daysInMonth);
    const receivedKg = received[line.material_id] ?? 0;
    const over = targetKg > 0 && receivedKg > targetKg;
    const info = meta(line.material_id);
    lines.push({
      material_id: line.material_id,
      parent_category: line.parent_category,
      material_type_name: info.name,
      catalog_material_id: info.catalog_material_id,
      category_code: info.category_code,
      unit: line.unit,
      has_target: true,
      target_kg: round2(targetKg),
      received_kg: round2(receivedKg),
      fulfillment_ratio: targetKg > 0 ? Math.round((receivedKg / targetKg) * 10000) / 10000 : null,
      excess_kg: targetKg > 0 ? round2(Math.max(0, receivedKg - targetKg)) : 0,
      is_over_target: over,
      is_excess_disallowed: over && !accepts,
    });
  }

  // Mass that arrived for waste types with no target line (the page hides these).
  Object.keys(received)
    .sort()
    .forEach((materialId) => {
      const receivedKg = received[materialId];
      if (targeted.has(materialId) || receivedKg <= 0) return;
      const info = meta(materialId);
      lines.push({
        material_id: materialId,
        parent_category: info.parent_category,
        material_type_name: info.name,
        catalog_material_id: info.catalog_material_id,
        category_code: info.category_code,
        unit: null,
        has_target: false,
        target_kg: 0,
        received_kg: round2(receivedKg),
        fulfillment_ratio: null,
        excess_kg: 0,
        is_over_target: false,
        is_excess_disallowed: false,
      });
    });

  return {
    period_start: naiveIso(new Date(startMs)),
    period_end_exclusive: naiveIso(new Date(endMs)),
    lines,
  };
}

export function computeFulfillment(recyclerOrgId: string, now = Date.now()): RecyclerAppetiteFulfillmentResponse {
  const config = getAppetite();
  const weekStart = mondayOf(0).getTime();
  const weekEnd = weekStart + 7 * DAY_MS;
  const monthStartMs = monthStart(0).getTime();
  const monthEndMs = monthStart(1).getTime();
  const daysInMonth = Math.max(1, Math.round((monthEndMs - monthStartMs) / DAY_MS));
  return {
    repeat: config.repeat ?? 'weekly',
    accepts_additional_quantity: config.accepts_additional_quantity !== false,
    this_week: buildPeriod('week', weekStart, weekEnd, daysInMonth, config, receivedKgByMaterial(recyclerOrgId, weekStart, weekEnd, now)),
    this_month: buildPeriod(
      'month',
      monthStartMs,
      monthEndMs,
      daysInMonth,
      config,
      receivedKgByMaterial(recyclerOrgId, monthStartMs, monthEndMs, now),
    ),
  };
}
